import { reducedMotion } from './motion'
import { buzz } from './haptic'
import { el } from './ui'

// Slide to confirm (Liam, 2026-09-24: "should the finish job be a slide to
// complete, is that more satisfying?"). A knob dragged along a bar: it cannot
// be done by a pocket or a wet thumb brushing the screen, and reaching the end
// feels like finishing. Let go early and the knob springs back. A plain tap
// nudges the knob to show which way it goes.
//
// A keyboard (Enter / Space) or a screen reader's activation confirms at once:
// both arrive as a click with no pointer behind it (event.detail === 0), and
// neither can slide.

const DONE_AT = 0.85

// tone 'lemon' (Liam, 12 Oct 2026, on Finish my day: "a bit too stark contrast wonder if it should be yellow then is
// satisfying if completed?"): a lemon bar with a slate knob, for a white sheet; the slate bar stays on the lemon
// started-job card, where lemon on lemon would disappear. Either way the bar fills lemon as it goes and turns green
// with a tick only when it is done.
export function slideToConfirm(opts: { label: string; doneLabel: string; onConfirm: () => void; tone?: 'lemon' }): HTMLElement {
  const track = el('div', { class: `slide-track${opts.tone === 'lemon' ? ' slide-lemon' : ''}`, role: 'button', tabindex: '0', 'aria-label': opts.label })
  const fill = el('span', { class: 'slide-fill', 'aria-hidden': 'true' })
  const text = el('span', { class: 'slide-text', 'aria-hidden': 'true' }, opts.label)
  const knob = el('span', { class: 'slide-knob ic ic-chevrons', 'aria-hidden': 'true' })
  track.append(fill, text, knob)

  let dragging = false
  let moved = false
  let done = false
  let startX = 0
  let dx = 0
  let max = 0

  const paint = (x: number): void => {
    knob.style.transform = `translateX(${x}px)`
    // Nothing filled at rest: the fill only starts once the knob moves.
    fill.style.width = x > 0 ? `${x + knob.offsetWidth + 12}px` : '0px'
    // The words are gone before the lemon fill reaches them (review: white on lemon is hard to read).
    text.style.opacity = String(Math.max(0, 1 - (max ? x / max : 0) * 2.2))
  }

  const confirm = (): void => {
    if (done) return
    done = true
    max = Math.max(0, track.clientWidth - knob.offsetWidth - 12)
    track.classList.remove('slide-dragging')
    track.classList.add('slide-done')
    text.textContent = opts.doneLabel
    paint(max)
    // After the paint, which fades the words out at the far end: "Finished" itself shows.
    text.style.opacity = '1'
    buzz('medium')
    window.setTimeout(opts.onConfirm, reducedMotion() ? 0 : 220)
  }

  knob.addEventListener('pointerdown', (e) => {
    if (done) return
    dragging = true
    max = Math.max(0, track.clientWidth - knob.offsetWidth - 12)
    startX = e.clientX - dx
    track.classList.add('slide-dragging')
    try {
      knob.setPointerCapture(e.pointerId)
    } catch {
      // older browsers: the move events still reach the knob while over it
    }
    e.preventDefault()
  })
  knob.addEventListener('pointermove', (e) => {
    if (!dragging) return
    dx = Math.min(max, Math.max(0, e.clientX - startX))
    if (dx > 4) moved = true
    paint(dx)
  })
  const release = (): void => {
    if (!dragging) return
    dragging = false
    track.classList.remove('slide-dragging')
    if (max && dx >= max * DONE_AT) {
      confirm()
      return
    }
    dx = 0
    paint(0)
  }
  knob.addEventListener('pointerup', release)
  // A slide the phone takes away (a call, a system swipe) springs back; it never finishes the job (Astra, 24 Oct 2026).
  knob.addEventListener('pointercancel', () => {
    if (!dragging) return
    dragging = false
    track.classList.remove('slide-dragging')
    dx = 0
    paint(0)
  })

  track.addEventListener('click', (e) => {
    if (done) return
    if (e.detail === 0) {
      confirm() // keyboard or a screen reader
      return
    }
    if (moved) {
      moved = false // the click that ends a slide is not a tap
      return
    }
    // A tap, not a slide: show which way it goes.
    if (!reducedMotion() && typeof knob.animate === 'function' && !dragging) {
      knob.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(34px)', offset: 0.45 }, { transform: 'translateX(0)' }], { duration: 520, easing: 'ease-out' })
    }
  })
  track.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      confirm()
    }
  })
  return track
}
